import { Alarm, AlarmAlertGroup } from '../../gql/generated';
import { formatMetricName } from '../../utils/formatMetric';
import { uniqueMachineNames } from '../../utils/machineDisplay';
import {
  SidePanel,
  SidePanelTab,
  SidePanelTabContent,
  SidePanelTabList,
  SidePanelTabs,
} from '../SidePanel';
import { AlarmChannelsTab } from './AlarmChannelsTab';
import { AlarmDetailsTab } from './AlarmDetailsTab';
import { AlarmEventLogTab } from './AlarmEventLogTab';
import { AlarmGraphTab } from './AlarmGraphTab';
import { AlarmGroupDetailsTab } from './AlarmGroupDetailsTab';

const alarmTabOrder = ['events', 'graph', 'details', 'channels'];

interface AlarmSidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set while the alert covers several signals. */
  group?: AlarmAlertGroup | null;
  /** One alert per signal of a group, or the single alert on its own. */
  alarms: Alarm[];
  /** The signal the panel opens narrowed to. */
  signal?: string;
}

/**
 * The side panel of a Pulse alert. It opens on the event log, which takes the
 * place of the earlier history. One signal of a multi-signal alert opens the
 * same panel, with the log narrowed to that signal.
 */
export function AlarmSidePanel({
  open,
  onOpenChange,
  group,
  alarms,
  signal,
}: AlarmSidePanelProps) {
  if (!alarms.length || !open) return null;

  const alarm = alarms[0];
  const names = uniqueMachineNames(alarms);
  // Every alert is a group in EDMS. Only one over several signals reads as one.
  const isMultiSignal = alarms.length > 1;

  // A multi-signal alert names how many machines it spans. One signal names them.
  const groupSubtitle = [
    `${names.length} ${names.length === 1 ? 'Machine' : 'Machines'}`,
    signal ? formatMetricName(signal) : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  const subtitle = isMultiSignal ? groupSubtitle : names.join(', ') || undefined;

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={group ? group.name : alarm.name}
      subtitle={subtitle}
      width='xl'
    >
      <SidePanelTabs
        defaultValue='events'
        className='flex h-full flex-col'
        enableSwipe
        tabOrder={alarmTabOrder}
      >
        <SidePanelTabList>
          <SidePanelTab value='events'>Event log</SidePanelTab>
          <SidePanelTab value='graph'>Graph</SidePanelTab>
          <SidePanelTab value='details'>Details</SidePanelTab>
          <SidePanelTab value='channels'>Channels</SidePanelTab>
        </SidePanelTabList>

        <SidePanelTabContent value='events' className='flex-1 overflow-y-auto'>
          <AlarmEventLogTab alarms={alarms} presetSignal={signal} />
        </SidePanelTabContent>

        <SidePanelTabContent value='graph' className='flex-1 overflow-y-auto'>
          <AlarmGraphTab alarms={alarms} signal={signal} />
        </SidePanelTabContent>

        <SidePanelTabContent value='details' className='flex-1 overflow-y-auto'>
          {group && isMultiSignal ? (
            <AlarmGroupDetailsTab group={group} alarms={alarms} />
          ) : (
            <AlarmDetailsTab alarm={alarm} />
          )}
        </SidePanelTabContent>

        <SidePanelTabContent
          value='channels'
          className='flex-1 overflow-y-auto'
        >
          <AlarmChannelsTab alarm={alarm} />
        </SidePanelTabContent>
      </SidePanelTabs>
    </SidePanel>
  );
}
